import { useState } from "react";
import { Link } from "react-router-dom";
import Badge from "components/base/Badge";
import { DealCardProps } from "interfaces/deal";
import { Card } from "react-bootstrap";
import { currencyFormat, dateFormatter } from "helpers/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCalendar, 
  faDollarSign, 
  faUser, 
  faBolt,
  faExclamationTriangle,
  faRocket,
  faChartLine 
} from "@fortawesome/free-solid-svg-icons";

const DealCardAICompact = ({ deal }: DealCardProps) => {
  // Calculate days until closing
  const getDaysUntilClosing = () => {
    if (!deal.deal_closing_date) return null;
    const closingDate = new Date(deal.deal_closing_date);
    const today = new Date();
    const days = Math.ceil((closingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const daysUntilClosing = getDaysUntilClosing();
  const isClosingSoon = daysUntilClosing !== null && daysUntilClosing >= 0 && daysUntilClosing <= 7;
  const isOverdue = daysUntilClosing !== null && daysUntilClosing < 0;
  const needsAction = !deal.deal_closing_date || !deal.deal_price || (daysUntilClosing !== null && daysUntilClosing > 30);

  // AI Score (mock - would come from backend)
  const aiScore = Math.floor(Math.random() * 30 + 70); // 70-100
  const aiScoreColor = aiScore >= 85 ? '#10b981' : aiScore >= 70 ? '#f59e0b' : '#ef4444';

  return (
    <Card 
      className="deal-card-ai-compact mb-2"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
        border: '1px solid rgba(0, 212, 255, 0.15)',
        borderRadius: '10px',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 212, 255, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.15)';
      }}
    >
      {/* AI Glow Effect */}
      <div
        style={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: '40px',
          height: '40px',
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.3) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(15px)',
          pointerEvents: 'none'
        }}
      />

      <Card.Body style={{ padding: '12px 14px' }}>
        {/* Header Row - Deal Name, AI Score and Value */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
            {/* AI Indicator */}
            <FontAwesomeIcon 
              icon={faBolt} 
              style={{ 
                color: '#00d4ff',
                fontSize: '10px',
                marginRight: '6px',
                filter: 'drop-shadow(0 0 3px rgba(0, 212, 255, 0.5))'
              }}
              className="pulse-animation"
            />
            
            <Link
              to={`/deals/${deal?.id}?type=activity`}
              className="deal-name"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#1f2937',
                textDecoration: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#00d4ff';
                e.currentTarget.style.textShadow = '0 0 4px rgba(0, 212, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#1f2937';
                e.currentTarget.style.textShadow = 'none';
              }}
            >
              {deal?.deal_name || 'Untitled Deal'}
            </Link>

            {/* AI Score Badge */}
            <div
              style={{
                marginLeft: '8px',
                padding: '2px 6px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${aiScoreColor}20 0%, ${aiScoreColor}10 100%)`,
                border: `1px solid ${aiScoreColor}30`,
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}
            >
              <FontAwesomeIcon 
                icon={faChartLine} 
                style={{ 
                  fontSize: '8px',
                  color: aiScoreColor
                }}
              />
              <span style={{ 
                fontSize: '10px',
                fontWeight: 700,
                color: aiScoreColor
              }}>
                {aiScore}%
              </span>
            </div>
          </div>
          
          {/* Deal Value with AI Glow */}
          <span 
            style={{
              fontSize: '14px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 10px rgba(16, 185, 129, 0.2)',
              marginLeft: '8px'
            }}
          >
            {currencyFormat(deal.deal_price || 0, { minimumFractionDigits: 0 })}
          </span>
        </div>

        {/* Info Row - Contact and Date with AI Insights */}
        <div className="d-flex align-items-center justify-content-between" style={{ fontSize: '11px' }}>
          <div className="d-flex align-items-center gap-3">
            {/* Customer with AI Enhancement */}
            <span className="d-flex align-items-center gap-1" style={{ color: '#64748b' }}>
              <FontAwesomeIcon 
                icon={faUser} 
                style={{ 
                  fontSize: '9px',
                  color: '#00d4ff',
                  filter: 'drop-shadow(0 0 2px rgba(0, 212, 255, 0.3))'
                }} 
              />
              <span style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap', 
                maxWidth: '100px' 
              }}>
                {deal?.customers?.[0]?.customer_name || 'No contact'}
              </span>
            </span>

            {/* MLS ID if available */}
            {deal.deal_mls_id && (
              <span 
                style={{ 
                  color: '#9ca3af',
                  fontSize: '10px',
                  padding: '1px 4px',
                  background: 'rgba(0, 212, 255, 0.05)',
                  borderRadius: '4px'
                }}
              >
                MLS: {deal.deal_mls_id}
              </span>
            )}
          </div>

          {/* Closing Date with AI Status Indicators */}
          <div className="d-flex align-items-center gap-2">
            {/* AI Status Indicators */}
            {needsAction && (
              <FontAwesomeIcon 
                icon={faExclamationTriangle}
                style={{
                  fontSize: '10px',
                  color: '#ef4444',
                  filter: 'drop-shadow(0 0 3px rgba(239, 68, 68, 0.5))',
                  animation: 'pulse 2s infinite'
                }}
                title="Needs attention"
              />
            )}
            
            {isClosingSoon && (
              <Badge 
                style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: 'none',
                  color: 'white',
                  fontWeight: 600,
                  boxShadow: '0 0 8px rgba(245, 158, 11, 0.3)'
                }}
              >
                <FontAwesomeIcon icon={faRocket} style={{ fontSize: '8px', marginRight: '2px' }} />
                {daysUntilClosing}d
              </Badge>
            )}
            
            {isOverdue && (
              <Badge 
                style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  color: 'white',
                  fontWeight: 600,
                  boxShadow: '0 0 8px rgba(239, 68, 68, 0.3)',
                  animation: 'pulse 2s infinite'
                }}
              >
                Overdue
              </Badge>
            )}
            
            <span 
              className="d-flex align-items-center gap-1"
              style={{ 
                color: '#64748b',
                padding: '2px 6px',
                background: 'rgba(0, 212, 255, 0.05)',
                borderRadius: '6px',
                border: '1px solid rgba(0, 212, 255, 0.1)'
              }}
            >
              <FontAwesomeIcon 
                icon={faCalendar} 
                style={{ 
                  fontSize: '9px',
                  color: '#00d4ff'
                }} 
              />
              {deal.deal_closing_date 
                ? new Date(deal.deal_closing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : 'No date'}
            </span>
          </div>
        </div>
      </Card.Body>

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        
        .pulse-animation {
          animation: pulse 2s infinite;
        }
      `}</style>
    </Card>
  );
};

export default DealCardAICompact;